import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Plus, Trash2, Pencil, Loader2, Download, X, CalendarClock, Upload } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const defaultRules = () => [6, 0, 1, 2, 3, 4, 5].map((i) => ({ weekday: i, mode: "normal", startTime: "", endTime: "" }));
const withRules = (rules) => defaultRules().map((d) => ({ ...d, ...((rules || []).find((r) => r.weekday === d.weekday) || {}) }));
const EMPTY = { name: "", closures: [], specialDays: [], source: "manual", rangeStart: "", rangeEnd: "", weekdayRules: defaultRules(), openDates: [] };
const derivedRange = (c) => {
  const pts = [...(c.closures || []).flatMap((x) => [x.start, x.end]), ...(c.specialDays || []).map((s) => s.date)].filter(Boolean).sort();
  return { rangeStart: c.rangeStart || pts[0] || "", rangeEnd: c.rangeEnd || pts[pts.length - 1] || "" };
};

export default function TermCalendars() {
  const { can } = useAuth();
  const [cals, setCals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [ics, setIcs] = useState({ url: "", text: "" });
  const [importing, setImporting] = useState(false);
  const [csv, setCsv] = useState("");
  const [importingCsv, setImportingCsv] = useState(false);

  const load = () => { setLoading(true); client.get("/term-calendars").then((r) => setCals(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(EMPTY); setIcs({ url: "", text: "" }); setOpen(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ name: c.name, closures: c.closures || [], specialDays: c.specialDays || [], source: c.source || "manual", ...derivedRange(c), weekdayRules: withRules(c.weekdayRules), openDates: c.openDates || [] }); setIcs({ url: "", text: "" }); setOpen(true); };

  const setRule = (i, k, v) => setForm((f) => ({ ...f, weekdayRules: f.weekdayRules.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const addClosure = () => setForm((f) => ({ ...f, closures: [...f.closures, { start: "", end: "", label: "" }] }));
  const setClosure = (i, k, v) => setForm((f) => ({ ...f, closures: f.closures.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const delClosure = (i) => setForm((f) => ({ ...f, closures: f.closures.filter((_, idx) => idx !== i) }));

  const addSpecial = () => setForm((f) => ({ ...f, specialDays: [...f.specialDays, { date: "", type: "half", note: "", areas: [] }] }));
  const setSpecial = (i, k, v) => setForm((f) => ({ ...f, specialDays: f.specialDays.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const delSpecial = (i) => setForm((f) => ({ ...f, specialDays: f.specialDays.filter((_, idx) => idx !== i) }));

  const doImport = async () => {
    if (!ics.url.trim() && !ics.text.trim()) { toast.error("Paste a calendar link or .ics text first"); return; }
    setImporting(true);
    try {
      const { data } = await client.post("/term-calendars/import-ics", { url: ics.url, text: ics.text });
      setForm((f) => ({ ...f, closures: [...f.closures, ...data.closures], source: "ics" }));
      toast.success(`Imported ${data.count} closure${data.count === 1 ? "" : "s"}`);
      setIcs({ url: "", text: "" });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setImporting(false); }
  };

  const onCsvFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => setCsv(String(rd.result || ""));
    rd.readAsText(f);
    e.target.value = "";
  };

  const doImportCsv = async () => {
    if (!csv.trim()) { toast.error("Paste or upload a CSV first"); return; }
    setImportingCsv(true);
    try {
      const { data } = await client.post("/term-calendars/import-csv", { text: csv });
      setForm((f) => ({ ...f, closures: [...f.closures, ...data.closures], specialDays: [...f.specialDays, ...data.specialDays], source: "csv", rangeStart: data.rangeStart || f.rangeStart, rangeEnd: data.rangeEnd || f.rangeEnd, openDates: [...new Set([...(f.openDates || []), ...(data.openDates || [])])].sort() }));
      toast.success(`Imported ${data.closures.length} closure range${data.closures.length === 1 ? "" : "s"} · ${data.specialDays.length} special day${data.specialDays.length === 1 ? "" : "s"}`);
      setCsv("");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setImportingCsv(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        closures: form.closures.filter((c) => c.start).map((c) => ({ start: c.start, end: c.end || c.start, label: c.label || "" })),
        specialDays: form.specialDays.filter((s) => s.date).map((s) => ({ date: s.date, type: s.type || "half", note: s.note || "", areas: (Array.isArray(s.areas) ? s.areas : String(s.areas || "").split(",")).map((a) => a.trim()).filter(Boolean) })),
        source: form.source,
        rangeStart: form.rangeStart || null, rangeEnd: form.rangeEnd || null,
        weekdayRules: form.weekdayRules.filter((r) => r.mode !== "normal"),
        openDates: form.openDates || [],
      };
      if (editId) {
        const { data } = await client.patch(`/term-calendars/${editId}`, payload);
        const a = data.applied || {};
        const extra = [a.shiftsCancelled ? `${a.shiftsCancelled} shift(s) cancelled` : "", a.shiftsRetimed ? `${a.shiftsRetimed} shift(s) re-timed` : "", a.shiftsRemovedNotOpen ? `${a.shiftsRemovedNotOpen} shift(s) removed (school closed)` : ""].filter(Boolean).join(", ");
        toast.success("Calendar updated" + (extra ? ` · ${extra}` : ""));
      }
      else { await client.post("/term-calendars", payload); toast.success("Calendar created"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!(await confirm({ title: "Delete term calendar", description: `Delete "${c.name}"? Sites using it will be detached (existing shifts stay).`, confirmText: "Delete", destructive: true }))) return;
    try { await client.delete(`/term-calendars/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="term-calendars-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Badge className="bg-teal-600/10 text-teal-700 border-teal-600/20 mb-1">iClean Connect</Badge>
          <h2 className="font-display text-2xl font-bold text-slate-900">Term Calendars</h2>
          <p className="text-sm text-slate-500">Define school terms &amp; closures once, attach to a site — recurring shifts skip holidays automatically.</p>
        </div>
        {can("site.manage") && <Button data-testid="term-new-btn" onClick={openNew} className="bg-brand hover:bg-brand-dark"><Plus className="h-4 w-4 mr-2" />New calendar</Button>}
      </div>

      {loading ? <p className="text-slate-500 text-sm">Loading…</p> : cals.length === 0 ? (
        <Card className="border-slate-200 p-10 text-center text-slate-400"><CalendarDays className="h-8 w-8 mx-auto mb-2" />No term calendars yet.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cals.map((c) => (
            <Card key={c.id} data-testid={`term-card-${c.id}`} className="border-slate-200 p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-teal-600" />{c.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5" data-testid={`term-summary-${c.id}`}>{(c.openDates || []).length ? `${c.openDates.length} school days · ` : <span className="text-amber-600">No school days uploaded · </span>}{(c.closures || []).length} closures · {(c.specialDays || []).length} special days</p>
                </div>
                {can("site.manage") && (
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" data-testid={`term-edit-${c.id}`} onClick={() => openEdit(c)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`term-delete-${c.id}`} onClick={() => remove(c)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-1">
                {(c.closures || []).slice(0, 4).map((cl, i) => <Badge key={i} variant="outline" className="text-[10px]">{cl.label || `${cl.start}→${cl.end}`}</Badge>)}
                {(c.closures || []).length > 4 && <Badge variant="outline" className="text-[10px]">+{c.closures.length - 4}</Badge>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="term-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit term calendar" : "New term calendar"}</DialogTitle>
            <DialogDescription>Add holiday closures (cleaning skipped) and special half-day / partial-clean days, then attach this calendar to a site.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input data-testid="term-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. St Mary's Primary 2025/26" className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="min-w-0"><Label>Term start</Label><Input type="date" data-testid="term-range-start" value={form.rangeStart || ""} onChange={(e) => setForm({ ...form, rangeStart: e.target.value })} className="mt-1.5 w-full min-w-0" /></div>
              <div className="min-w-0"><Label>Term end</Label><Input type="date" data-testid="term-range-end" value={form.rangeEnd || ""} onChange={(e) => setForm({ ...form, rangeEnd: e.target.value })} className="mt-1.5 w-full min-w-0" /></div>
            </div>
            <p className="text-[11px] text-slate-500 -mt-2">First and last school day. "Follow school schedule" shifts are created up to the term end.</p>

            <div className="rounded-lg border border-slate-200 p-3 space-y-2 bg-emerald-50/40">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><Upload className="h-3.5 w-3.5" />Import school schedule (.csv)</p>
              <p className="text-[11px] text-slate-500">Columns: <code>Date (YYYY-MM-DD), Day, Day Type, Notes</code>. Listed dates = school open; any gaps become closures (cleaning skipped). Dates with a note become special days.</p>
              <div className="flex items-center gap-2">
                <input type="file" accept=".csv,text/csv" onChange={onCsvFile} data-testid="term-csv-file" className="text-xs file:mr-2 file:rounded-md file:border-0 file:bg-slate-200 file:px-3 file:py-1.5 file:text-xs file:font-semibold" />
              </div>
              <textarea data-testid="term-csv-text" value={csv} onChange={(e) => setCsv(e.target.value)} rows={3} placeholder="…or paste CSV rows here" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" />
              <Button size="sm" variant="outline" data-testid="term-csv-import" onClick={doImportCsv} disabled={importingCsv}>{importingCsv ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}Import from CSV</Button>
            </div>

            <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="term-weekday-rules">
              <Label>Weekly rules</Label>
              <p className="text-[11px] text-slate-500">Closed = never cleaned that weekday. Different time = that weekday always uses its own hours. Saving also updates existing future shifts.</p>
              {form.weekdayRules.map((r, i) => (
                <div key={r.weekday} className="grid grid-cols-[5.5rem_1fr] gap-2 items-center">
                  <span className="text-xs font-medium text-slate-700">{DAY_NAMES[r.weekday]}</span>
                  <div className="flex flex-wrap gap-2 items-center min-w-0">
                    <Select value={r.mode} onValueChange={(v) => setRule(i, "mode", v)}>
                      <SelectTrigger data-testid={`term-rule-mode-${r.weekday}`} className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="custom">Different time</SelectItem>
                      </SelectContent>
                    </Select>
                    {r.mode === "custom" && (<>
                      <Input type="time" data-testid={`term-rule-start-${r.weekday}`} value={r.startTime} onChange={(e) => setRule(i, "startTime", e.target.value)} className="h-8 w-24 min-w-0 px-2 text-xs" />
                      <Input type="time" data-testid={`term-rule-end-${r.weekday}`} value={r.endTime} onChange={(e) => setRule(i, "endTime", e.target.value)} className="h-8 w-24 min-w-0 px-2 text-xs" />
                    </>)}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-slate-200 p-3 space-y-2 bg-slate-50/50">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><Download className="h-3.5 w-3.5" />Import from school calendar (.ics)</p>
              <Input data-testid="term-ics-url" value={ics.url} onChange={(e) => setIcs({ ...ics, url: e.target.value })} placeholder="Paste calendar link (https:// or webcal://)" />
              <textarea data-testid="term-ics-text" value={ics.text} onChange={(e) => setIcs({ ...ics, text: e.target.value })} rows={3} placeholder="…or paste raw .ics text" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              <Button size="sm" variant="outline" data-testid="term-ics-import" onClick={doImport} disabled={importing}>{importing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}Import closures</Button>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>Holidays &amp; closures (cleaning skipped)</Label><Button size="sm" variant="ghost" data-testid="term-add-closure" onClick={addClosure} className="text-brand"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button></div>
              {form.closures.length === 0 && <p className="text-xs text-slate-400">No closures yet.</p>}
              {form.closures.map((c, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-center" data-testid={`term-closure-${i}`}>
                  <Input type="date" value={c.start} onChange={(e) => setClosure(i, "start", e.target.value)} />
                  <Input type="date" value={c.end} onChange={(e) => setClosure(i, "end", e.target.value)} />
                  <Input value={c.label} onChange={(e) => setClosure(i, "label", e.target.value)} placeholder="Label (e.g. Half term)" />
                  <Button size="icon" variant="ghost" onClick={() => delClosure(i)} className="text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>Special days (half-day / partial clean)</Label><Button size="sm" variant="ghost" data-testid="term-add-special" onClick={addSpecial} className="text-brand"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button></div>
              {form.specialDays.length === 0 && <p className="text-xs text-slate-400">No special days yet.</p>}
              {form.specialDays.map((s, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1.6fr_auto] gap-2 items-center" data-testid={`term-special-${i}`}>
                  <Input type="date" value={s.date} onChange={(e) => setSpecial(i, "date", e.target.value)} />
                  <Select value={s.type} onValueChange={(v) => setSpecial(i, "type", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="half">Half day</SelectItem>
                      <SelectItem value="partial">Partial clean</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input value={Array.isArray(s.areas) ? s.areas.join(", ") : s.areas} onChange={(e) => setSpecial(i, "areas", e.target.value)} placeholder={s.type === "partial" ? "Exclude e.g. Nursery, Hall" : "Note (optional)"} />
                  <Button size="icon" variant="ghost" onClick={() => delSpecial(i)} className="text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="term-save" onClick={save} disabled={busy || !form.name} className="bg-brand hover:bg-brand-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editId ? "Save" : "Create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
